/**
 * Spielerische Dashboard-Vorschau für die Partner-Landingpage.
 * - Pulsierender Live-Punkt
 * - Hochzählende Zahlen beim Mount (rAF)
 * - Rotierender Activity-Ticker mit fiktiven Beispielen
 */
import { useEffect, useRef, useState } from "react";

const KPIS = [
  { label: "Klicks (30 Tage)", value: 1248, format: (n: number) => n.toLocaleString("de-DE") },
  { label: "Empfehlungen", value: 37, format: (n: number) => n.toLocaleString("de-DE") },
  { label: "Offene Provision", value: 289.2, format: (n: number) => `${n.toFixed(2).replace(".", ",")} €` },
  { label: "Diesen Monat ausgezahlt", value: 412.8, format: (n: number) => `${n.toFixed(2).replace(".", ",")} €` },
];

const TICKER = [
  "Neue Empfehlung von Sandra K. 🎉",
  "Maria H. hat MusiKonzept gebucht 🎶",
  "+7,80 € Provision gutgeschrieben ✨",
  "Neuer Klick aus Hamburg",
  "Julia B. ist jetzt Mitglied 💚",
  "+7,80 € Provision gutgeschrieben ✨",
];

function useCountUp(target: number, durationMs = 1100): number {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    let raf = 0;
    const tick = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return value;
}

const KpiRow = ({ kpi }: { kpi: typeof KPIS[number] }) => {
  const animated = useCountUp(kpi.value);
  return (
    <div className="flex items-center justify-between rounded-xl border bg-background/50 px-4 py-3">
      <span className="text-sm text-muted-foreground">{kpi.label}</span>
      <span className="text-lg font-bold tabular-nums">{kpi.format(animated)}</span>
    </div>
  );
};

const LiveDashboardPreview = () => {
  const [tickerIndex, setTickerIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setTickerIndex((i) => (i + 1) % TICKER.length);
    }, 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative">
      <div className="absolute -inset-8 rounded-3xl bg-gradient-to-tr from-primary/30 via-accent/15 to-transparent blur-3xl animate-pulse" />
      <div className="relative rounded-3xl border bg-card p-6 shadow-xl sm:p-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
            </span>
            Beispiel-Dashboard
          </div>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Vorschau
          </span>
        </div>

        <div className="space-y-3">
          {KPIS.map((kpi) => (
            <KpiRow key={kpi.label} kpi={kpi} />
          ))}
        </div>

        <div className="mt-4 overflow-hidden rounded-xl bg-primary/10 px-4 py-3 text-sm">
          <div
            key={tickerIndex}
            className="animate-fade-in font-medium text-primary"
          >
            {TICKER[tickerIndex]}
          </div>
        </div>

        <p className="mt-3 text-center text-[10px] text-muted-foreground">
          Beispielzahlen – so könnte dein eigenes Dashboard aussehen.
        </p>
      </div>
    </div>
  );
};

export default LiveDashboardPreview;
